/**
 * InventoryPage — Sprint 5.2D + STOCK-SONOFF-INVENTORY-5 (estoque por técnico).
 *
 * Aba "Estoque" no NOC. Ciclo completo: estoque geral → com técnico →
 * reservado → instalado, além de devolvido / defeito / RMA / perdido.
 *
 * - Dashboard de cards (contagem por status + offline).
 * - Filtros por status / offline.
 * - Tabela: QR · Device · Modelo/Lote · Status · Técnico · Cliente · HB.
 * - Ações contextuais por status, TODAS com motivo obrigatório
 *   (Distribuir, Transferir, Devolver, Defeito, Perdido, RMA, Corrigir).
 * - Histórico de movimentações por item (audit por inventory.id).
 *
 * Polling de 15s. Não interfere com o mapa, o installer nem o IXC.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import NavIcon from "../nav/NavIcon.jsx";
import {
  assignInventoryItem,
  bulkCreateInventory,
  correctInventoryItem,
  createInventoryItem,
  getInventoryMovements,
  inventoryStatusAction,
  listInventory,
  listAccounts,
  reserveInventoryItem,
  transferInventoryItem,
  updateInventoryItem,
} from "../lib/api.js";
import { formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";
import Can from "../components/Can.jsx";
import { CAP } from "../lib/rbac.js";
import QrScannerModal from "../components/QrScannerModal.jsx";
import {
  isValidPhysicalQr,
  normalizePhysicalQr,
  PHYSICAL_QR_ERROR,
} from "../lib/physicalQr.js";

const POLL_MS = 15000;

// Filtros da barra superior. 'offline' é um pseudo-status.
const FILTERS = [
  { key: "", label: "inventory.filterAll" },
  { key: "stock", label: "inventory.statusStock" },
  { key: "com_tecnico", label: "inventory.statusComTecnico" },
  { key: "reserved", label: "inventory.statusReserved" },
  { key: "installed", label: "inventory.statusInstalled" },
  { key: "offline", label: "inventory.filterOffline" },
  { key: "devolvido", label: "inventory.statusDevolvido" },
  { key: "defeito", label: "inventory.statusDefeito" },
  { key: "rma", label: "inventory.statusRma" },
  { key: "lost", label: "inventory.statusLost" },
];

const STATUS_STYLE = {
  stock: "text-noc-accent bg-noc-accent/10 border-noc-accent/30",
  com_tecnico: "text-noc-accent bg-noc-accent/10 border-noc-accent/40",
  reserved: "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
  installed: "text-noc-ok bg-noc-ok/10 border-noc-ok/30",
  devolvido: "text-noc-muted bg-noc-bg/40 border-noc-border",
  defeito: "text-noc-orange bg-noc-orange/10 border-noc-orange/30",
  maintenance: "text-noc-orange bg-noc-orange/10 border-noc-orange/30",
  rma: "text-noc-orange bg-noc-orange/10 border-noc-orange/40",
  lost: "text-noc-err bg-noc-err/10 border-noc-err/30",
};

// Modelos/SKUs válidos (espelha INVENTORY_MODELS do backend).
const MODELS = ["client", "tower-core", "tower-energy", "commercial"];

// Ações contextuais por status (label i18n → tipo de modal).
const ROW_ACTIONS = {
  stock: [
    { key: "assign", label: "inventory.btnAssign" },
    { key: "defect", label: "inventory.btnDefect" },
    { key: "lost", label: "inventory.btnLost" },
  ],
  com_tecnico: [
    { key: "transfer", label: "inventory.btnTransfer" },
    { key: "return", label: "inventory.btnReturn" },
    { key: "defect", label: "inventory.btnDefect" },
    { key: "lost", label: "inventory.btnLost" },
  ],
  reserved: [
    { key: "return", label: "inventory.btnReturn" },
    { key: "defect", label: "inventory.btnDefect" },
    { key: "lost", label: "inventory.btnLost" },
  ],
  installed: [
    { key: "defect", label: "inventory.btnDefect" },
    { key: "rma", label: "inventory.btnRma" },
  ],
  devolvido: [{ key: "readmit", label: "inventory.btnReadmit" }],
  defeito: [
    { key: "rma", label: "inventory.btnRma" },
    { key: "readmit", label: "inventory.btnReadmit" },
  ],
  rma: [{ key: "readmit", label: "inventory.btnReadmit" }],
  lost: [{ key: "readmit", label: "inventory.btnReadmit" }],
  maintenance: [{ key: "readmit", label: "inventory.btnReadmit" }],
};

function StatusPill({ status }) {
  const cls = STATUS_STYLE[status] || "text-noc-muted bg-noc-bg/40";
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}
    >
      {t("inventory.status_" + status) || status}
    </span>
  );
}

export default function InventoryPage() {
  const [data, setData] = useState({
    items: [], total: 0, counts: {}, offline_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("");
  // Técnicos (users role=field) — dropdown de distribuição + coluna.
  const [fieldUsers, setFieldUsers] = useState([]);
  // Modais
  const [modal, setModal] = useState(null);
  // 'new' | 'reserve' | 'edit' | 'assign' | 'transfer' |
  // 'return' | 'defect' | 'lost' | 'rma' | 'readmit' | 'movements'
  const [activeItem, setActiveItem] = useState(null);

  const buildFilters = useCallback(() => {
    if (filter === "offline") return { offline: true };
    if (filter) return { status: filter };
    return {};
  }, [filter]);

  const load = useCallback((signal) => {
    listInventory(buildFilters(), { signal })
      .then((d) => {
        setData(d);
        setErr(null);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, [buildFilters]);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  // Técnicos de campo (uma vez; best-effort — sem eles a coluna mostra id).
  // Review adversarial (P0): GET /api/admin/accounts devolve
  // {accounts:[{id: users.id, email, role}]} — o shape/id que o /assign
  // espera. (GET /api/admin/users devolvia TenantUserOut sem email e com
  // PK de tenant_users → dropdown vazio e assign 409.)
  useEffect(() => {
    listAccounts()
      .then((d) => {
        const accounts = d?.accounts || [];
        setFieldUsers(accounts.filter((u) => u.role === "field"));
      })
      .catch((e) => {
        console.warn("inventory: falha ao listar técnicos", e);
      });
  }, []);

  const techByUserId = useMemo(() => {
    const m = {};
    for (const u of fieldUsers) m[String(u.id)] = u.email || u.name || u.id;
    return m;
  }, [fieldUsers]);

  const reload = () => {
    const ac = new AbortController();
    load(ac.signal);
  };

  function openAction(item, key) {
    setActiveItem(item);
    setModal(key);
  }

  const counts = data.counts || {};

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label mb-2">Gestão de ativos</div>
          <h1 className="text-3xl font-semibold tracking-tight text-noc-text">
            {t("inventory.title")}
          </h1>
          <p className="text-sm text-noc-muted mt-1">
            Controle estoque, distribuição técnica e ciclo de vida dos equipamentos.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Can capability={CAP.INVENTORY_WRITE}>
          <button
            className="btn-primary h-10 px-4 inline-flex items-center gap-2"
            onClick={() => setModal("new")}
          >
            <NavIcon name="box" size={15} />
            {t("inventory.btnNew")}
          </button>
          <button
            className="btn-ghost h-10 px-3"
            onClick={() => setModal("bulk")}
          >
            {t("inventory.btnBulk")}
          </button>
          <button
            className="h-10 px-3 rounded-xl border border-noc-warn/30
                       hover:bg-noc-warn/10 text-noc-warn text-sm"
            onClick={() => setModal("reserve")}
          >
            {t("inventory.btnReserve")}
          </button>
          </Can>
        </div>
      </header>

      {/* Dashboard de cards — 8 status + offline */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        <DashCard label={t("inventory.statusStock")}
                  value={counts.stock || 0} tone="sky" />
        <DashCard label={t("inventory.statusComTecnico")}
                  value={counts.com_tecnico || 0} tone="sky" />
        <DashCard label={t("inventory.statusReserved")}
                  value={counts.reserved || 0} tone="amber" />
        <DashCard label={t("inventory.statusInstalled")}
                  value={counts.installed || 0} tone="emerald" />
        <DashCard label={t("inventory.filterOffline")}
                  value={data.offline_count || 0} tone="red" />
        <DashCard label={t("inventory.statusDevolvido")}
                  value={counts.devolvido || 0} tone="slate" />
        <DashCard label={t("inventory.statusDefeito")}
                  value={counts.defeito || 0} tone="orange" />
        <DashCard label={t("inventory.statusRma")}
                  value={counts.rma || 0} tone="orange" />
        <DashCard label={t("inventory.statusLost")}
                  value={counts.lost || 0} tone="red" />
      </div>

      {/* Filtros */}
      <div className="glass-card p-3 flex gap-1.5 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            className={
              "text-xs px-3 py-1.5 rounded-lg border " +
              (filter === f.key
                ? "border-noc-accent/30 text-noc-accent bg-noc-accent/10"
                : "border-noc-border text-noc-muted hover:bg-noc-bg/40")
            }
          >
            {t(f.label)}
          </button>
        ))}
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded p-2">
          {err}
        </div>
      )}

      {/* Tabela */}
      <div className="glass-card !p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[11px] uppercase tracking-[0.12em] text-noc-muted bg-noc-bg/30 border-b border-noc-divider">
            <tr>
              <th className="text-left font-medium py-1.5 px-2">QR</th>
              <th className="text-left font-medium py-1.5 px-2">Dispositivo</th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("inventory.colModel")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("inventory.colStatus")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("inventory.colTech")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("inventory.colCustomer")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">HB</th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("inventory.colActions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="text-sm text-noc-muted/70
                                           italic py-3 px-2">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr>
                <td colSpan={8} className="text-sm text-noc-muted/60
                                           italic py-3 px-2">
                  {t("inventory.empty")}
                </td>
              </tr>
            )}
            {data.items.map((it) => (
              <tr key={it.id}
                  className="border-b border-noc-divider/70 last:border-0
                             hover:bg-noc-panel2/35 transition-colors">
                <td className="py-1.5 px-2 font-mono text-noc-accent">
                  {it.qr_serial}
                </td>
                <td className="py-1.5 px-2 font-mono text-noc-muted">
                  {it.device_id || "—"}
                </td>
                <td className="py-1.5 px-2 text-noc-muted">
                  {it.model || "—"}
                  {it.batch && (
                    <span className="block text-[10px] text-noc-muted/60">
                      {it.batch}
                    </span>
                  )}
                </td>
                <td className="py-1.5 px-2">
                  <StatusPill status={it.status} />
                  {it.offline && (
                    <span className="ml-1 text-[10px] text-noc-err">
                      ● {t("inventory.offlineTag")}
                    </span>
                  )}
                </td>
                <td className="py-1.5 px-2 text-noc-muted">
                  {it.assigned_user_id
                    ? (techByUserId[String(it.assigned_user_id)] ||
                       String(it.assigned_user_id).slice(0, 8))
                    : (it.installer || "—")}
                </td>
                <td className="py-1.5 px-2">
                  {it.customer_id ? `#${it.customer_id}` : "—"}
                </td>
                <td className="py-1.5 px-2 text-noc-muted">
                  {it.last_hb_at ? formatRelative(it.last_hb_at) : "—"}
                </td>
                <td className="py-1.5 px-2">
                  <div className="flex gap-1 flex-wrap">
                    <Can
                      capability={CAP.INVENTORY_WRITE}
                      fallback={<span className="text-[10px] text-noc-muted/60">—</span>}
                    >
                    {(ROW_ACTIONS[it.status] || []).map((a) => (
                      <button
                        key={a.key}
                        className="text-[10px] px-1.5 py-0.5 rounded border
                                   border-noc-border hover:bg-noc-bg/50"
                        onClick={() => openAction(it, a.key)}
                      >
                        {t(a.label)}
                      </button>
                    ))}
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-border hover:bg-noc-bg/50"
                      onClick={() => { setActiveItem(it); setModal("edit"); }}
                    >
                      {t("inventory.btnEdit")}
                    </button>
                    </Can>
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-border text-noc-muted
                                 hover:bg-noc-bg/50"
                      onClick={() => openAction(it, "movements")}
                    >
                      {t("inventory.btnHistory")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-noc-muted/70">
        {t("inventory.total")}: {data.total}
      </div>

      {/* ── Modais ─────────────────────────────────────────────────── */}
      {modal === "new" && (
        <InventoryFormModal
          title={t("inventory.btnNew")}
          fields={["qr_serial", "mac", "model", "batch", "notes"]}
          onClose={() => setModal(null)}
          onSubmit={async (body) => {
            await createInventoryItem(body);
            setModal(null);
            reload();
          }}
        />
      )}
      {modal === "bulk" && (
        <BulkModal
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); reload(); }}
        />
      )}
      {modal === "reserve" && (
        <InventoryFormModal
          title={t("inventory.btnReserve")}
          fields={[
            "qr_serial", "customer_id", "contract_id",
            "installer", "team", "scheduled_at", "notes",
          ]}
          onClose={() => setModal(null)}
          onSubmit={async (body) => {
            await reserveInventoryItem(body);
            setModal(null);
            reload();
          }}
        />
      )}
      {modal === "edit" && activeItem && (
        <InventoryFormModal
          title={t("inventory.btnEdit")}
          fields={[
            "device_id", "mac", "fw", "model", "batch",
            "installer", "team", "location", "notes",
          ]}
          initial={activeItem}
          onClose={() => { setModal(null); setActiveItem(null); }}
          onSubmit={async (body) => {
            await updateInventoryItem(activeItem.id, body);
            setModal(null);
            setActiveItem(null);
            reload();
          }}
        />
      )}
      {["assign", "transfer"].includes(modal) && activeItem && (
        <ReasonActionModal
          title={t(modal === "assign"
            ? "inventory.btnAssign" : "inventory.btnTransfer")}
          item={activeItem}
          techs={fieldUsers}
          needTech
          onClose={() => { setModal(null); setActiveItem(null); }}
          onSubmit={async ({ techId, reason }) => {
            if (modal === "assign") {
              await assignInventoryItem(activeItem.id, {
                assigned_user_id: techId, reason,
              });
            } else {
              await transferInventoryItem(activeItem.id, {
                to_user_id: techId, reason,
              });
            }
            setModal(null);
            setActiveItem(null);
            reload();
          }}
        />
      )}
      {["return", "defect", "lost", "rma"].includes(modal) && activeItem && (
        <ReasonActionModal
          title={t({
            return: "inventory.btnReturn",
            defect: "inventory.btnDefect",
            lost: "inventory.btnLost",
            rma: "inventory.btnRma",
          }[modal])}
          item={activeItem}
          onClose={() => { setModal(null); setActiveItem(null); }}
          onSubmit={async ({ reason }) => {
            await inventoryStatusAction(activeItem.id, modal, reason);
            setModal(null);
            setActiveItem(null);
            reload();
          }}
        />
      )}
      {modal === "readmit" && activeItem && (
        <ReasonActionModal
          title={t("inventory.btnReadmit")}
          item={activeItem}
          onClose={() => { setModal(null); setActiveItem(null); }}
          onSubmit={async ({ reason }) => {
            await correctInventoryItem(activeItem.id, {
              new_status: "stock", reason,
            });
            setModal(null);
            setActiveItem(null);
            reload();
          }}
        />
      )}
      {modal === "movements" && activeItem && (
        <MovementsModal
          item={activeItem}
          techByUserId={techByUserId}
          onClose={() => { setModal(null); setActiveItem(null); }}
        />
      )}
    </div>
  );
}

function DashCard({ label, value, tone }) {
  const toneCls = {
    sky: "border-noc-accent/30 text-noc-accent",
    amber: "border-noc-warn/30 text-noc-warn",
    emerald: "border-noc-ok/30 text-noc-ok",
    orange: "border-noc-orange/30 text-noc-orange",
    red: "border-noc-err/30 text-noc-err",
    slate: "border-noc-border text-noc-muted",
  }[tone] || "border-noc-border text-noc-text";
  return (
    <div className={`stat-card ${toneCls}`}>
      <div className="text-2xl font-semibold mt-2">{value}</div>
      <div className="section-label">{label}</div>
    </div>
  );
}

/**
 * Modal de ação com MOTIVO OBRIGATÓRIO (espelha o padrão de Proteção).
 * needTech: mostra dropdown de técnicos (Distribuir / Transferir).
 */
function ReasonActionModal({ title, item, techs, needTech, onClose, onSubmit }) {
  const [techId, setTechId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const canSubmit = reason.trim().length > 0 && (!needTech || techId);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ techId, reason: reason.trim() });
    } catch (e) {
      setError(e?.message || String(e));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center
                    justify-center z-50 p-4"
         onClick={onClose}>
      <div className="bg-noc-bg border border-noc-border rounded-lg
                      p-4 w-full max-w-md space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-noc-text">
          {title} — <span className="font-mono">{item.qr_serial}</span>
        </h3>
        {needTech && (
          <div>
            <label className="text-[11px] text-noc-muted block mb-0.5">
              {t("inventory.colTech")}
            </label>
            <select
              value={techId}
              onChange={(e) => setTechId(e.target.value)}
              className="w-full text-xs bg-noc-bg/60 border border-noc-border
                         rounded px-2 py-1 text-noc-text"
            >
              <option value="">—</option>
              {(techs || []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email || u.name || u.id}
                </option>
              ))}
            </select>
            {(techs || []).length === 0 && (
              <p className="text-[10px] text-noc-warn mt-0.5">
                {t("inventory.noFieldUsers")}
              </p>
            )}
          </div>
        )}
        <div>
          <label className="text-[11px] text-noc-muted block mb-0.5">
            {t("inventory.reasonLabel")} *
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="w-full text-xs bg-noc-bg/60 border border-noc-border
                       rounded px-2 py-1 text-noc-text"
            placeholder={t("inventory.reasonPlaceholder")}
          />
        </div>
        {error && (
          <div className="text-[11px] text-noc-err bg-noc-err/10
                          border border-noc-err/30 rounded p-1.5">
            {error}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-border text-noc-muted hover:bg-noc-bg/40"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-accent/30 text-noc-accent bg-noc-accent/10
                       hover:bg-noc-accent/10 disabled:opacity-50"
            disabled={busy || !canSubmit}
            onClick={submit}
          >
            {busy ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Histórico de movimentações do item (audit por inventory.id). */
function MovementsModal({ item, techByUserId, onClose }) {
  const [movs, setMovs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getInventoryMovements(item.id)
      .then((d) => setMovs(d.items || []))
      .catch((e) => setError(e?.message || String(e)));
  }, [item.id]);

  function fmtWho(m) {
    const a = m.actor || "—";
    return techByUserId[a] || a;
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center
                    justify-center z-50 p-4"
         onClick={onClose}>
      <div className="bg-noc-bg border border-noc-border rounded-lg
                      p-4 w-full max-w-lg space-y-2"
           onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-noc-text">
          {t("inventory.movTitle")} — <span className="font-mono">{item.qr_serial}</span>
        </h3>
        {error && (
          <div className="text-[11px] text-noc-err">{error}</div>
        )}
        {movs === null && !error && (
          <div className="text-xs text-noc-muted italic">
            {t("common.loading")}
          </div>
        )}
        {movs !== null && movs.length === 0 && (
          <div className="text-xs text-noc-muted/60 italic">
            {t("inventory.movEmpty")}
          </div>
        )}
        <div className="max-h-80 overflow-y-auto space-y-1.5">
          {(movs || []).map((m) => (
            <div key={m.id}
                 className="border border-noc-border/60 rounded p-1.5
                            text-[11px]">
              <div className="flex justify-between gap-2">
                <span className="font-mono text-noc-accent">
                  {t("inventory.mov_" + m.action) || m.action}
                </span>
                <span className="text-noc-muted/70">
                  {formatRelative(m.created_at)}
                </span>
              </div>
              <div className="text-noc-muted">
                {m.old_value?.status && m.new_value?.status &&
                  m.old_value.status !== m.new_value.status && (
                  <span className="mr-2">
                    {m.old_value.status} → {m.new_value.status}
                  </span>
                )}
                <span className="text-noc-muted/70">{fmtWho(m)}</span>
              </div>
              {m.notes && (
                <div className="text-noc-text/90 mt-0.5">“{m.notes}”</div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-border text-noc-muted hover:bg-noc-bg/40"
            onClick={onClose}
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Cadastro em massa: um QR por linha ("QR" ou "QR,MAC") + lote/modelo. */
function BulkModal({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [batch, setBatch] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const items = text
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const [qr, mac] = l.split(/[,;\t]/).map((x) => x && x.trim());
          const row = { qr_serial: normalizePhysicalQr(qr || "") };
          if (mac) row.mac = mac;
          return row;
        });
      const body = { items };
      if (batch.trim()) body.batch = batch.trim();
      if (model) body.model = model;
      const res = await bulkCreateInventory(body);
      setResult(res);
      setBusy(false);
      if ((res.skipped || []).length === 0) onDone();
    } catch (e) {
      setError(e?.message || String(e));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center
                    justify-center z-50 p-4"
         onClick={onClose}>
      <div className="bg-noc-bg border border-noc-border rounded-lg
                      p-4 w-full max-w-md space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-noc-text">
          {t("inventory.btnBulk")}
        </h3>
        <div>
          <label className="text-[11px] text-noc-muted block mb-0.5">
            {t("inventory.bulkLines")}
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder={"25082200178100\n25082200178101,AA:BB:CC:DD:EE:01"}
            className="w-full text-xs bg-noc-bg/60 border border-noc-border
                       rounded px-2 py-1 text-noc-text font-mono"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] text-noc-muted block mb-0.5">
              {t("inventory.field_batch")}
            </label>
            <input
              type="text"
              value={batch}
              onChange={(e) => setBatch(e.target.value)}
              className="w-full text-xs bg-noc-bg/60 border border-noc-border
                         rounded px-2 py-1 text-noc-text"
            />
          </div>
          <div>
            <label className="text-[11px] text-noc-muted block mb-0.5">
              {t("inventory.field_model")}
            </label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full text-xs bg-noc-bg/60 border border-noc-border
                         rounded px-2 py-1 text-noc-text"
            >
              <option value="">—</option>
              {MODELS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>
        {error && (
          <div className="text-[11px] text-noc-err bg-noc-err/10
                          border border-noc-err/30 rounded p-1.5">
            {error}
          </div>
        )}
        {result && (
          <div className="text-[11px] text-noc-muted">
            {t("inventory.bulkCreated")}: {(result.created || []).length} ·{" "}
            {t("inventory.bulkSkipped")}: {(result.skipped || []).length}
            {(result.skipped || []).map((s) => (
              <div key={s.qr_serial} className="text-noc-warn">
                {s.qr_serial}: {s.reason}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-border text-noc-muted hover:bg-noc-bg/40"
            onClick={result ? onDone : onClose}
          >
            {result ? t("common.close") : t("common.cancel")}
          </button>
          {!result && (
            <button
              className="text-xs px-2.5 py-1 rounded border
                         border-noc-accent/30 text-noc-accent bg-noc-accent/10
                         hover:bg-noc-accent/10 disabled:opacity-50"
              disabled={busy || !text.trim()}
              onClick={submit}
            >
              {busy ? t("common.loading") : t("common.save")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Modal de formulário genérico — usado para Novo / Reservar / Editar.
 * Renderiza um input por campo em `fields` ('model' vira select).
 * STOCK-5: NÃO expõe mais 'status' (mutação de status = ações com motivo).
 */
function InventoryFormModal({ title, fields, initial, onClose, onSubmit }) {
  const [values, setValues] = useState(() => {
    const v = {};
    for (const f of fields) v[f] = (initial && initial[f]) || "";
    return v;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // PROVISIONING-PHYSICAL-QR-CAMERA-1 — scanner de câmera p/ o QR físico.
  const [scanOpen, setScanOpen] = useState(false);
  const hasQr = fields.includes("qr_serial");

  function setField(k, val) {
    setValues((prev) => ({ ...prev, [k]: val }));
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      // Só envia campos preenchidos.
      const body = {};
      for (const [k, v] of Object.entries(values)) {
        if (v === "" || v === null || v === undefined) continue;
        // QR físico: normaliza (remove espaços) antes de enviar.
        body[k] = k === "qr_serial" ? normalizePhysicalQr(v) : v;
      }
      await onSubmit(body);
    } catch (e) {
      setError(e?.message || String(e));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center
                    justify-center z-50 p-4"
         onClick={onClose}>
      <div className="bg-noc-bg border border-noc-border rounded-lg
                      p-4 w-full max-w-md space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-noc-text">{title}</h3>
        <div className="space-y-2">
          {fields.map((f) =>
            f === "qr_serial" ? (
              <div key={f}>
                <label className="text-[11px] text-noc-muted block mb-0.5">
                  QR físico do Sonoff
                </label>
                <div className="flex gap-1">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={values[f]}
                    onChange={(e) => setField(f, e.target.value)}
                    placeholder="25082200178100"
                    className="flex-1 text-xs bg-noc-bg/60 border border-noc-border
                               rounded px-2 py-1 text-noc-text font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setScanOpen(true)}
                    title="Escanear com câmera"
                    className="text-xs px-2 py-1 rounded border border-noc-border
                               text-noc-muted hover:text-noc-text"
                  >
                    📷 Escanear
                  </button>
                </div>
                {values[f] && !isValidPhysicalQr(values[f]) && (
                  <p className="text-[10px] text-noc-warn mt-0.5">
                    {PHYSICAL_QR_ERROR}
                  </p>
                )}
              </div>
            ) : f === "model" ? (
              <div key={f}>
                <label className="text-[11px] text-noc-muted block mb-0.5">
                  {t("inventory.field_model")}
                </label>
                <select
                  value={values[f]}
                  onChange={(e) => setField(f, e.target.value)}
                  className="w-full text-xs bg-noc-bg/60 border
                             border-noc-border rounded px-2 py-1
                             text-noc-text"
                >
                  <option value="">—</option>
                  {MODELS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div key={f}>
                <label className="text-[11px] text-noc-muted block mb-0.5">
                  {t("inventory.field_" + f) || f}
                </label>
                <input
                  type={f === "scheduled_at" ? "datetime-local" : "text"}
                  value={values[f]}
                  onChange={(e) => setField(f, e.target.value)}
                  className="w-full text-xs bg-noc-bg/60 border
                             border-noc-border rounded px-2 py-1
                             text-noc-text"
                />
              </div>
            )
          )}
        </div>
        {hasQr && scanOpen && (
          <QrScannerModal
            onDetected={(value) => {
              setField("qr_serial", normalizePhysicalQr(value));
              setScanOpen(false);
            }}
            onManual={() => setScanOpen(false)}
            onClose={() => setScanOpen(false)}
          />
        )}
        {error && (
          <div className="text-[11px] text-noc-err bg-noc-err/10
                          border border-noc-err/30 rounded p-1.5">
            {error}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-border text-noc-muted
                       hover:bg-noc-bg/40"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-accent/30 text-noc-accent
                       bg-noc-accent/10 hover:bg-noc-accent/10
                       disabled:opacity-50"
            disabled={busy}
            onClick={submit}
          >
            {busy ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
